import { useState } from "react";
import { useModal } from "hooks/useModal";
import { SelectFolderForUpload } from "components/SelectFolderForUpload";
import { SavedToInkleDocsModal } from "components/SavedToInkleDocsModal/SavedToInkleDocsModal";
import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Table from "components/DesignSystem/Table/V2/Table";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Search } from "components/DesignSystem/Search/Search";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Chip } from "components/DesignSystem/Chips/Chips";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Loader from "components/DesignSystem/Loader/loader";
import Card from "components/DesignSystem/Card/Card";
import { Button } from "components/DesignSystem/Button/Button";
import { SolidCheck } from "components/icons/SolidCheck";
import ToolTip from "components/design/toolTip";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CaretDown } from "components/icons/CaretDown";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useFilters } from "hooks/useFilter";
import { useToast } from "hooks/useToast";
import {
  useGetSITOverviewQuery,
  useGetSITSalesDataQuery,
  useGetSITPayrollDataQuery,
  useGetSITPropertyDataQuery,
  usePatchSITTaxableIncomeMutation,
  useExportSITOverviewMutation,
} from "store/apis/stateIncomeTax";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { DOWNLOAD_FORMAT } from "constants/bookkeeping";
import { SIT_NEXUS_FILTER, SITNexusFilter } from "constants/stateIncomeTax";
import { openLink } from "utils/openLink";
import { useGetEntityAllStateDataQuery } from "store/apis/entity";
import {
  SITOverviewItem,
  SITOverviewSortBy,
  SITSource,
} from "types/Models/stateIncomeTax";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import BarePriceInput from "components/PriceInput/BarePriceInput";
import { BackendError } from "types/utils/error";
import { formatAmount } from "utils/foramtAmount";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "react-table-8.10.7";

const STATE_COL_WIDTH = 180;
const NEXUS_COL_WIDTH = 80;

const columnHelper = createColumnHelper<SITOverviewItem>();

const TABS = {
  JURISDICTIONS: "jurisdictions",
  SALES_DATA: "sales-data",
  PAYROLL: "payroll",
  PROPERTY: "property",
} as const;

type DataSource = {
  id: string;
  title: string;
  description: string;
  hasDone: boolean;
  onAdd: () => void;
};

const DataSourceStep = ({
  step,
  title,
  description,
  hasDone,
  isLast,
  onAdd,
}: DataSource & { step: number; isLast: boolean }) => (
  <div className="t-flex t-gap-4">
    <div className="t-flex t-flex-col t-items-center">
      <div
        className={classNames(
          "t-flex t-h-8 t-w-8 t-flex-shrink-0 t-items-center t-justify-center t-rounded-full t-border-2 t-border-solid t-text-body-sm t-font-semibold",
          {
            "t-border-green-70 t-bg-green-0 t-text-green-70": hasDone,
            "t-border-neutral-20 t-bg-surface t-text-text-30": !hasDone,
          }
        )}
      >
        {hasDone ? <SolidCheck size="16" color="currentColor" /> : step}
      </div>
      {!isLast && (
        <div
          className={classNames("t-mt-2 t-w-0.5 t-flex-1", {
            "t-bg-green-20": hasDone,
            "t-bg-neutral-10": !hasDone,
          })}
        />
      )}
    </div>

    <div
      className={classNames(
        "t-flex t-flex-1 t-items-center t-gap-4 t-rounded-lg t-border t-border-solid t-p-4",
        {
          "t-border-green-20 t-bg-green-0": hasDone,
          "t-border-neutral-10 t-bg-surface": !hasDone,
          "t-mb-4": !isLast,
        }
      )}
    >
      <div className="t-flex t-flex-1 t-flex-col t-gap-0.5">
        <span className="t-text-body-sm t-font-semibold t-text-text-100">
          {title}
        </span>
        <span className="t-text-body-sm t-text-text-30">{description}</span>
      </div>
      <Button
        customType={hasDone ? "secondary" : "primary"}
        size="small"
        onClick={onAdd}
      >
        <div className="t-w-20">{hasDone ? "View" : "Add Data"}</div>
      </Button>
    </div>
  </div>
);

type Props = {
  season: number;
  onTabChange: (tab: string) => void;
};

const SOURCE_OPTIONS = [
  { value: "JURISDICTION", label: "Jurisdiction" },
  { value: "SALES_DATA", label: "Sales Data" },
  { value: "PAYROLL", label: "Payroll" },
  { value: "PROPERTY", label: "Property" },
];

const NEXUS_OPTIONS: { value: SITNexusFilter; label: string }[] = [
  { value: SIT_NEXUS_FILTER.NEXUS_ELIGIBLE, label: "Nexus Eligible" },
  { value: SIT_NEXUS_FILTER.NEXUS_INELIGIBLE, label: "Nexus Ineligible" },
  { value: SIT_NEXUS_FILTER.NEXUS_REVIEW, label: "Nexus Required Review" },
];

const SORT_ID_MAP: Record<string, SITOverviewSortBy> = {
  state_name: "state",
  revenue: "revenue",
  gross_pay: "gross_pay",
  taxable_income: "taxable_income",
  apportionment_pct: "apportionment_pct",
  tax_rate: "tax_rate",
  tax_due: "tax_due",
  overall_nexus: "overall_nexus",
};

const NullCell = () => <span className="t-text-body-sm t-text-text-30">—</span>;

type TaxableIncomeCellProps = {
  row: SITOverviewItem;
  season: number;
  groupId: string;
  entityId: string;
};

const TaxableIncomeCell = ({
  row,
  season,
  groupId,
  entityId,
}: TaxableIncomeCellProps) => {
  const { alertToast } = useToast();
  const hasAmount = Boolean(row.taxable_income);
  const [isEditing, setIsEditing] = useState(!hasAmount);
  const [value, setValue] = useState(row.taxable_income ?? "");

  const [patchTaxableIncome, { isLoading }] =
    usePatchSITTaxableIncomeMutation();

  const startEdit = () => {
    setValue(row.taxable_income ?? "");
    setIsEditing(true);
  };

  const handleSave = async () => {
    const newValue = value.trim() === "" ? null : value.trim();
    const prevValue = row.taxable_income ?? null;
    if (newValue === prevValue) {
      setIsEditing(false);
      return;
    }
    try {
      await patchTaxableIncome({
        groupId,
        entityId,
        season,
        state_uuid: row.state_uuid,
        taxable_income: newValue,
      }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
      setValue(row.taxable_income ?? "");
    } finally {
      setIsEditing(!hasAmount);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape" && hasAmount) {
      setValue(row.taxable_income ?? "");
      setIsEditing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="t-flex t-justify-end">
        <Loader size="small" />
      </div>
    );
  }

  if (isEditing) {
    return (
      <BarePriceInput
        autoFocus={hasAmount}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="t-w-40"
      />
    );
  }

  return (
    <button
      className="all:unset t-flex t-w-full t-cursor-pointer t-items-center t-justify-end"
      onClick={startEdit}
    >
      <span className="t-text-body-sm t-text-text-60">
        <AmountSuperScript amount={Number(row.taxable_income)} />
      </span>
    </button>
  );
};

export const SITOverview = ({ season, onTabChange }: Props) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [sorting, setSorting] = useState<SortingState>([]);
  const { successToast, alertToast } = useToast();

  const { values, updateFilter } = useFilters({
    initialValue: {
      search: "",
      source: [] as SITSource[],
      nexus: [] as string[],
    },
    useQueryParams: true,
  });

  const sort_by = sorting[0] ? SORT_ID_MAP[sorting[0].id] : undefined;
  const sort_order = sorting?.[0]?.desc ? "desc" : "asc";
  const selectedSources = values.source || [];
  const selectedNexus = values.nexus || [];
  const source = selectedSources.length > 0 ? selectedSources : undefined;
  const search = values.search;

  const isNexusEligibleActive = selectedNexus.includes(
    SIT_NEXUS_FILTER.NEXUS_ELIGIBLE
  );
  const isNexusIneligibleActive = selectedNexus.includes(
    SIT_NEXUS_FILTER.NEXUS_INELIGIBLE
  );
  const isNexusReviewActive = selectedNexus.includes(
    SIT_NEXUS_FILTER.NEXUS_REVIEW
  );

  const allNexusOptions = Object.values(SIT_NEXUS_FILTER);
  const isAllSelected = selectedNexus.length === allNexusOptions.length;
  const nexus =
    selectedNexus.length === 0 || isAllSelected
      ? undefined
      : selectedNexus.join(",");

  const {
    data,
    isLoading: isOverviewLoading,
    isSuccess,
  } = useGetSITOverviewQuery(
    { groupId, entityId, season, search, source, nexus, sort_by, sort_order },
    { skip: !groupId || !entityId }
  );

  const { data: jurisdictions = [], isLoading: isJurisdictionsLoading } =
    useGetEntityAllStateDataQuery(
      { groupId, entityId },
      { skip: !groupId || !entityId }
    );

  const { data: salesData, isLoading: isSalesDataLoading } =
    useGetSITSalesDataQuery(
      { groupId, entityId, season },
      { skip: !groupId || !entityId }
    );

  const { data: payrollData, isLoading: isPayrollDataLoading } =
    useGetSITPayrollDataQuery(
      { groupId, entityId, season },
      { skip: !groupId || !entityId }
    );

  const { data: propertyData, isLoading: isPropertyDataLoading } =
    useGetSITPropertyDataQuery(
      { groupId, entityId, season },
      { skip: !groupId || !entityId }
    );

  const [exportSITOverview, { isLoading: isExporting }] =
    useExportSITOverviewMutation();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const selectFolderModal = useModal();
  const savedToInkleDocsModal = useModal();
  const [savedDocumentId, setSavedDocumentId] = useState<string>();

  const isLoading =
    isOverviewLoading ||
    isJurisdictionsLoading ||
    isSalesDataLoading ||
    isPayrollDataLoading ||
    isPropertyDataLoading;

  const onExport = async (exportToMail: boolean, folderId?: string) => {
    try {
      const result = await exportSITOverview({
        groupId,
        entityId,
        season,
        action: DOWNLOAD_FORMAT.DOWNLOAD_EXCEL,
        export_to_mail: exportToMail,
        folder_id: folderId,
      }).unwrap();

      if (exportToMail) {
        successToast({ message: "Report will be sent to your email" });
        return;
      }

      if ("document_id" in result) {
        if (folderId) {
          setSavedDocumentId(result.document_id);
          savedToInkleDocsModal.open();
        } else {
          const { download_url } = await getPreviewUrl({
            groupId,
            fileId: result.document_id,
          }).unwrap();
          openLink(download_url);
          successToast({ message: "Report downloaded" });
        }
      }
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const jurisdictionCount = jurisdictions?.length;
  const hasSalesData = Boolean(salesData);
  const hasPayrollData = Boolean(payrollData);
  const hasPropertyData = Boolean(propertyData?.data?.length);

  const sources: DataSource[] = [
    {
      id: "jurisdictions",
      title: "Jurisdictions",
      description:
        "Define the states you file in and the applicable tax rules.",
      hasDone: jurisdictionCount > 0,
      onAdd: () => onTabChange(TABS.JURISDICTIONS),
    },
    {
      id: "sales_data",
      title: "Sales Data",
      description:
        "Upload revenue by state to calculate nexus and apportion income.",
      hasDone: hasSalesData,
      onAdd: () => onTabChange(TABS.SALES_DATA),
    },
    {
      id: "payroll",
      title: "Payroll",
      description:
        "Upload payroll by state to factor employee compensation into apportionment.",
      hasDone: hasPayrollData,
      onAdd: () => onTabChange(TABS.PAYROLL),
    },
    {
      id: "property",
      title: "Property",
      description:
        "Add owned and rented property per state to complete the apportionment formula.",
      hasDone: hasPropertyData,
      onAdd: () => onTabChange(TABS.PROPERTY),
    },
  ];

  const rows = data?.data ?? [];
  const hasActiveFilters =
    (search && search.length > 0) ||
    selectedSources.length > 0 ||
    selectedNexus.length > 0;

  const columns = [
    columnHelper.accessor((row) => row, {
      id: "state_name",
      size: STATE_COL_WIDTH + NEXUS_COL_WIDTH,
      header: (header) => {
        return (
          <div className="t-flex t-w-full t-justify-between t-pr-2">
            <HeadCellContent
              title="State"
              sort={{
                nextSortOrder: header.column.getNextSortingOrder(),
                isSorted: header.column.getIsSorted(),
                sortingHandler: header.column.getToggleSortingHandler(),
              }}
            />
            <HeadCellContent title="Nexus" align="right" />
          </div>
        );
      },
      cell: (info) => {
        const row = info.getValue();
        return (
          <div className="t-flex t-items-center t-justify-between t-gap-2 t-pr-2">
            <ToolTip text={row.state_name}>
              <span className="t-block t-max-w-36 t-truncate t-text-body-sm t-font-semibold t-text-text-60">
                {row.state_name}
              </span>
            </ToolTip>
            {row.needs_review ? (
              <Tag tagType="yellow" icon={false} size="small">
                Required Review
              </Tag>
            ) : row.overall_nexus ? (
              <Tag tagType="green" icon={false} size="small">
                Yes
              </Tag>
            ) : (
              <Tag tagType="red" icon={false} size="small">
                No
              </Tag>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("revenue", {
      id: "revenue",
      size: 130,
      header: (header) => (
        <HeadCellContent
          title="Revenue"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const isSalesState = info.row.original.is_sales_data_state;
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {isSalesState && value ? formatAmount(Number(value)) : "—"}
          </span>
        );
      },
    }),
    columnHelper.accessor("gross_pay", {
      id: "gross_pay",
      size: 130,
      header: (header) => (
        <HeadCellContent
          title="Payroll"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        const isPayrollState = info.row.original.is_payroll_state;
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {isPayrollState && value ? formatAmount(Number(value)) : "—"}
          </span>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        const owned = row.owned_property ? Number(row.owned_property) : null;
        const rented = row.rented_property ? Number(row.rented_property) : null;
        if (owned === null && rented === null) return null;
        return (owned ?? 0) + 8 * (rented ?? 0);
      },
      {
        id: "property",
        size: 130,
        enableSorting: false,
        header: () => <HeadCellContent title="Property" align="right" />,
        cell: (info) => {
          const value = info.getValue();
          if (value === null)
            return (
              <span className="t-block t-text-right">
                <NullCell />
              </span>
            );
          return (
            <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
              {formatAmount(value)}
            </span>
          );
        },
      }
    ),
    columnHelper.accessor((row) => row, {
      id: "taxable_income",
      size: 140,
      header: (header) => (
        <HeadCellContent
          title="Taxable Income"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const row = info.getValue();
        if (!row.overall_nexus)
          return (
            <span className="t-block t-text-right">
              <NullCell />
            </span>
          );
        return (
          <TaxableIncomeCell
            row={row}
            season={season}
            groupId={groupId}
            entityId={entityId}
          />
        );
      },
    }),
    columnHelper.accessor("apportionment_pct", {
      id: "apportionment_pct",
      size: 170,
      header: (header) => (
        <HeadCellContent
          title="Apportionment %"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        if (!value || !info.row.original.overall_nexus)
          return (
            <span className="t-block t-text-right">
              <NullCell />
            </span>
          );
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {parseFloat(value).toFixed(2)}%
          </span>
        );
      },
    }),
    columnHelper.accessor("tax_rate", {
      id: "tax_rate",
      size: 140,
      header: (header) => (
        <HeadCellContent
          title="Tax Rate"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        if (!value || !info.row.original.overall_nexus)
          return (
            <span className="t-block t-text-right">
              <NullCell />
            </span>
          );
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {parseFloat(value).toFixed(2)}%
          </span>
        );
      },
    }),
    columnHelper.accessor("flat_amount", {
      id: "flat_amount",
      size: 140,
      enableSorting: false,
      header: () => <HeadCellContent title="Fixed Charge" align="right" />,
      cell: (info) => {
        const value = info.getValue();
        if (!value || !info.row.original.overall_nexus)
          return (
            <span className="t-block t-text-right">
              <NullCell />
            </span>
          );
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {formatAmount(Number(value))}
          </span>
        );
      },
    }),
    columnHelper.accessor("tax_due", {
      id: "tax_due",
      size: 140,
      header: (header) => (
        <HeadCellContent
          title="Tax Due"
          align="right"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: header.column.getToggleSortingHandler(),
          }}
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        if (!value || !info.row.original.overall_nexus)
          return (
            <span className="t-block t-text-right">
              <NullCell />
            </span>
          );
        return (
          <span className="t-block t-text-right t-text-body-sm t-tabular-nums t-text-text-60">
            {formatAmount(Number(value))}
          </span>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnPinning: { left: ["state_name"] },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
  });

  const isTableEmpty = rows.length === 0;

  const DataSourcesCard = (
    <Card.Root borderRounded="lg">
      <Card.Header>
        <div className="t-flex t-flex-col t-gap-0.5">
          <Card.Title>Data Sources</Card.Title>
          <span className="t-text-body-sm t-text-text-30">
            Add data to get complete overview of state-wise income tax
          </span>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="t-flex t-flex-col">
          {sources.map((source, index) => (
            <DataSourceStep
              key={source.id}
              step={index + 1}
              isLast={index === sources.length - 1}
              {...source}
            />
          ))}
        </div>
      </Card.Body>
    </Card.Root>
  );

  const ExportButton = (
    <Dropdown.Root>
      <Dropdown.Trigger asChild className="t-group">
        <div>
          <Button size="small" isLoading={isExporting} disabled={isExporting}>
            <div className="t-flex t-items-center t-gap-1.5">
              Export
              <div className="group-data-state-open:t-rotate-180">
                <CaretDown />
              </div>
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content sideOffset={4} align="end" side="bottom">
          <ExportOptions
            onMailExport={() => onExport(true)}
            onLocalDownload={() => onExport(false)}
            onSaveInInkleDocs={() => selectFolderModal.open()}
          />
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );

  const FiltersRow = (
    <div className="t-flex t-items-center t-justify-between t-gap-2">
      <div className="t-flex t-items-center t-gap-2">
        <Search
          placeholder="Search state..."
          value={values.search ?? ""}
          onChange={(e) => updateFilter("search", e.target.value)}
        />
        <Filter.Root
          defaultValue="SOURCE"
          title="Filters"
          capsule={
            <>
              {selectedSources.length > 0 && (
                <Chip
                  isActive
                  filterType="SOURCE"
                  onRemove={() => updateFilter("source", [] as SITSource[])}
                >
                  Source ({selectedSources.length})
                </Chip>
              )}
              <Chip
                isActive={isNexusEligibleActive}
                isFixedFilter
                filterType="NEXUS"
                onClick={() =>
                  updateFilter(
                    "nexus",
                    isNexusEligibleActive
                      ? selectedNexus.filter(
                          (n) => n !== SIT_NEXUS_FILTER.NEXUS_ELIGIBLE
                        )
                      : [...selectedNexus, SIT_NEXUS_FILTER.NEXUS_ELIGIBLE]
                  )
                }
                onRemove={() =>
                  updateFilter(
                    "nexus",
                    selectedNexus.filter(
                      (n) => n !== SIT_NEXUS_FILTER.NEXUS_ELIGIBLE
                    )
                  )
                }
              >
                Nexus Eligible
              </Chip>
              {isNexusIneligibleActive && (
                <Chip
                  isActive={isNexusIneligibleActive}
                  filterType="NEXUS"
                  onClick={() =>
                    updateFilter(
                      "nexus",
                      isNexusIneligibleActive
                        ? selectedNexus.filter(
                            (n) => n !== SIT_NEXUS_FILTER.NEXUS_INELIGIBLE
                          )
                        : [...selectedNexus, SIT_NEXUS_FILTER.NEXUS_INELIGIBLE]
                    )
                  }
                  onRemove={() =>
                    updateFilter(
                      "nexus",
                      selectedNexus.filter(
                        (n) => n !== SIT_NEXUS_FILTER.NEXUS_INELIGIBLE
                      )
                    )
                  }
                >
                  Nexus Ineligible
                </Chip>
              )}
              <Chip
                isActive={isNexusReviewActive}
                isFixedFilter
                filterType="NEXUS"
                onClick={() =>
                  updateFilter(
                    "nexus",
                    isNexusReviewActive
                      ? selectedNexus.filter(
                          (n) => n !== SIT_NEXUS_FILTER.NEXUS_REVIEW
                        )
                      : [...selectedNexus, SIT_NEXUS_FILTER.NEXUS_REVIEW]
                  )
                }
                onRemove={() =>
                  updateFilter(
                    "nexus",
                    selectedNexus.filter(
                      (n) => n !== SIT_NEXUS_FILTER.NEXUS_REVIEW
                    )
                  )
                }
              >
                Nexus Required Review
              </Chip>
            </>
          }
        >
          <Filter.Portal>
            <Filter.List>
              <Filter.ListItem value="SOURCE">Source</Filter.ListItem>
              <Filter.ListItem value="NEXUS">Nexus</Filter.ListItem>
            </Filter.List>
            <Filter.Body value="SOURCE" block>
              <MultiSelectFilter
                options={SOURCE_OPTIONS}
                selected={selectedSources}
                isSearchable={false}
                onChange={(newValues) =>
                  updateFilter("source", newValues as SITSource[])
                }
              />
            </Filter.Body>
            <Filter.Body value="NEXUS" block>
              <MultiSelectFilter
                options={NEXUS_OPTIONS}
                selected={selectedNexus}
                isSearchable={false}
                onChange={(newValues) =>
                  updateFilter("nexus", newValues as SITNexusFilter[])
                }
              />
            </Filter.Body>
          </Filter.Portal>
        </Filter.Root>
      </div>
      {ExportButton}
    </div>
  );

  return (
    <div className="t-mt-3">
      <Async.Root
        isLoading={isLoading}
        isSuccess={isSuccess}
        isEmpty={isTableEmpty && !hasActiveFilters}
      >
        <Async.Empty>
          <div className="t-flex t-flex-col t-gap-5">
            {DataSourcesCard}
            <div className="t-flex t-items-center t-justify-center t-py-16 t-text-body-sm t-text-text-30">
              No data yet. Add jurisdictions or upload data to get started.
            </div>
          </div>
        </Async.Empty>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-5">
            {DataSourcesCard}
            <div className="t-flex t-flex-col t-gap-4">
              {FiltersRow}
              {isTableEmpty ? (
                <div className="t-flex t-items-center t-justify-center t-py-16 t-text-body-sm t-text-text-30">
                  No states match your search or filters.
                </div>
              ) : (
                <Table.Container>
                  <Table.Content style={{ minWidth: table.getTotalSize() }}>
                    <Table.Head>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Table.HeadRow key={headerGroup.id}>
                          {headerGroup.headers.map((header) => {
                            const isPinned = header.column.getIsPinned();
                            return (
                              <Table.HeadCell
                                key={header.id}
                                style={{
                                  width: header.getSize(),
                                  ...(isPinned && {
                                    position: "sticky",
                                    left: header.column.getStart("left"),
                                    zIndex: 1,
                                  }),
                                }}
                                isUpperCase={false}
                                className={classNames("t-group", {
                                  "t-bg-surface-lighter-grey t-drop-shadow-table-left-pin":
                                    isPinned,
                                })}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext()
                                    )}
                              </Table.HeadCell>
                            );
                          })}
                        </Table.HeadRow>
                      ))}
                    </Table.Head>
                    <Table.Body>
                      {table.getRowModel().rows.map((row) => (
                        <Table.Row key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            const isPinned = cell.column.getIsPinned();
                            return (
                              <Table.Cell
                                key={cell.id}
                                style={{
                                  width: cell.column.getSize(),
                                  ...(isPinned && {
                                    position: "sticky",
                                    left: cell.column.getStart("left"),
                                    zIndex: 1,
                                  }),
                                }}
                                className={classNames({
                                  "t-bg-white t-drop-shadow-table-left-pin group-hover:!t-bg-surface-lighter-grey":
                                    isPinned,
                                })}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Content>
                </Table.Container>
              )}
            </div>
          </div>
        </Async.Success>
      </Async.Root>
      {selectFolderModal.isOpen && (
        <SelectFolderForUpload
          showModal={selectFolderModal.isOpen}
          closeModal={selectFolderModal.close}
          onUpload={(folderId) => onExport(false, folderId)}
        />
      )}
      {savedToInkleDocsModal.isOpen && savedDocumentId && (
        <SavedToInkleDocsModal
          isOpen={savedToInkleDocsModal.isOpen}
          close={savedToInkleDocsModal.close}
          fileId={savedDocumentId}
          reportType="State Income Tax Overview"
        />
      )}
    </div>
  );
};
